import { Sprite } from 'three';

import type { EventManager } from '../event-manager.class';
import { setupDragTrackingCfn } from './setup-drag-tracking.cfn';

const SELECTION_GROUP_TYPES = ['island-selection-labels', 'appliance-selection-labels'];

/**
 * Remove selection label groups from the scene when a label is clicked
 */
const removeSelectionLabels = (clickedObject: any): void => {
	const labelsGroup = clickedObject.parent;
	if (!labelsGroup || !SELECTION_GROUP_TYPES.includes(labelsGroup.userData?.type)) return;

	const objectHolder = labelsGroup.parent;
	if (!objectHolder) return;

	// Remove all selection groups (both island and appliance)
	const toRemove = objectHolder.children.filter(
		(child: any) => SELECTION_GROUP_TYPES.includes(child.userData?.type)
	);
	for (const group of toRemove) {
		group.traverse((obj: any) => {
			if (obj instanceof Sprite) {
				obj.material.map?.dispose();
				obj.material.dispose();
			}
		});
		objectHolder.remove(group);
	}
};

/**
 * Setup event listeners
 */
export const setupEventListenersCfn = (self: EventManager, _params: Record<string, never>): void => {
	const config = self.internalConfig;
	const { domElement } = config;

	// Drag tracking, so a camera move that ends over an object is not read as
	// a click on it.
	setupDragTrackingCfn(self, {});

	// Click handler
	const handleClick = (event: MouseEvent): void => {
		if (self.internalPointerDragged) return;

		const result = self.raycast(event.clientX, event.clientY);
		const callbacks = self.internalCallbacks;

		if (result) {
			self.internalSelectedObjectId = result.objectId;
			self.internalLastSelectionNormal = result.normal;
			self.internalLastSelectionObjectId = result.objectId;

			callbacks.onSelect?.(result);

			// Auto-hide selection labels when one is clicked
			if (result.object.userData?.type === 'selection-label') {
				removeSelectionLabels(result.object);
			}
		} else {
			self.internalLastSelectionNormal = null;
			self.internalLastSelectionObjectId = null;
		}
	};
	self.internalHandleClick = handleClick;

	// Double-click handler
	const handleDoubleClick = (event: MouseEvent): void => {
		if (self.internalPointerDragged) return;

		const result = self.raycast(event.clientX, event.clientY);
		const callbacks = self.internalCallbacks;

		if (result) {
			self.internalLastSelectionNormal = result.normal;
			self.internalLastSelectionObjectId = result.objectId;

			const addHighlight = event.shiftKey;
			callbacks.onDoubleClick?.(result, addHighlight);
		} else {
			self.internalLastSelectionNormal = null;
			self.internalLastSelectionObjectId = null;
		}
	};
	self.internalHandleDoubleClick = handleDoubleClick;

	// Right-click (context menu) handler
	const handleContextMenu = (event: MouseEvent): void => {
		event.preventDefault();

		// A right-button drag pans the camera; the contextmenu that follows the
		// release is part of that gesture, not a request for the menu.
		if (self.internalPointerDragged) return;

		const result = self.raycast(event.clientX, event.clientY);
		const callbacks = self.internalCallbacks;

		if (result) {
			self.internalLastSelectionNormal = result.normal;
			self.internalLastSelectionObjectId = result.objectId;

			callbacks.onRightClick?.(result);
		}
	};
	self.internalHandleContextMenu = handleContextMenu;

	// Touch handler for double-tap
	const DOUBLE_TAP_DELAY = 300;
	const DOUBLE_TAP_DISTANCE = 30;

	const handleTouchStart = (event: TouchEvent): void => {

		if (event.touches.length !== 1) return;

		const touch = event.touches[0];
		const currentTime = Date.now();
		const timeSinceLastTap = currentTime - self.internalLastTapTime;
		const distanceFromLastTap = Math.sqrt(
			(touch.clientX - self.internalLastTapX) ** 2 + (touch.clientY - self.internalLastTapY) ** 2
		);

		// Check for double tap
		if (timeSinceLastTap < DOUBLE_TAP_DELAY && distanceFromLastTap < DOUBLE_TAP_DISTANCE) {
			event.preventDefault();

			const result = self.raycast(touch.clientX, touch.clientY);
			if (result) {
				self.internalCallbacks.onDoubleClick?.(result, false);
			}

			self.internalLastTapTime = 0;
		} else {
			self.internalLastTapTime = currentTime;
			self.internalLastTapX = touch.clientX;
			self.internalLastTapY = touch.clientY;
		}
	};
	self.internalHandleTouchStart = handleTouchStart;

	// Mouse move handler for hover. Raycasting the whole scene per mousemove is far more work than a
	// frame needs, so moves within one frame collapse into the last position.
	let hoverFrame: number | null = null;
	let hoverX = 0;
	let hoverY = 0;
	const handleMouseMove = (event: MouseEvent): void => {
		const callbacks = self.internalCallbacks;
		if (!callbacks.onHover) return;

		hoverX = event.clientX;
		hoverY = event.clientY;
		if (hoverFrame !== null) return;
		hoverFrame = requestAnimationFrame(() => {
			hoverFrame = null;
			self.internalCallbacks.onHover?.(self.raycast(hoverX, hoverY));
		});
	};
	self.internalHandleMouseMove = handleMouseMove;

	// Attach listeners
	domElement.addEventListener('click', handleClick);
	domElement.addEventListener('dblclick', handleDoubleClick);
	domElement.addEventListener('contextmenu', handleContextMenu);
	domElement.addEventListener('touchstart', handleTouchStart, { passive: false });
	domElement.addEventListener('mousemove', handleMouseMove);
};
